import { TenantFormattingService } from '../../../core/services/tenant-formatting.service';
import { AppDatePipe } from '../../pipes/app-date.pipe';
import { AfterViewChecked, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription, firstValueFrom, forkJoin, of, switchMap, map } from 'rxjs';
import { FilesService } from '../../../core/services/files.service';
import { FileModel } from '../../../core/models/file.model';
import { ChatAttachmentComponent } from './chat-attachment.component';
import { CommunicationService, OrderMessage } from '../../../core/services/communication.service';
import { getApiErrorMessage } from '../../../core/utils/api-error-message';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faPaperPlane, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { communicationLimits, chatDisplay } from '../../../core/config/communication.config';
import { MessagePage, communicationKinds } from '../../../core/models/communication.model';

interface SelectedAttachment {
  file: File;
  uploaded?: FileModel;
}

interface PendingMessageRequest {
  id: string;
  signature: string;
}
@Component({
  selector: 'app-order-chat', imports: [FormsModule, AppDatePipe, FaIconComponent, ChatAttachmentComponent],
  templateUrl: './order-chat.component.html',
  styleUrl: './order-chat.component.css'
})
export class OrderChatComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  readonly formatting = inject(TenantFormattingService);
  readonly sendIcon = faPaperPlane;
  readonly attachmentIcon = faPaperclip;
  readonly limits = communicationLimits;
  readonly display = chatDisplay;
  selectedFiles: SelectedAttachment[] = [];
  private destroyed = false;
  private conversationVersion = 0;
  private conversationClientId?: string;
  private loadedOrderId = "";
  private loadRequest?: Subscription;
  @Input({ required: true })
  orderId!: string;
  @Input()
  active = true;
  private initialized = false;
  @ViewChild('timeline')
  timeline?: ElementRef<HTMLElement>;
  messages: OrderMessage[] = [];
  draft = '';
  error = '';
  loading = false;
  sending = false;
  hasMore = false;
  private pendingSend?: PendingMessageRequest;
  private subscriptions = new Subscription();
  private refreshPending = false;
  private pendingScroll?: {
    kind: 'bottom';
  } | {
    kind: 'history';
    height: number;
    top: number;
  };
  constructor(public communication: CommunicationService, private files: FilesService) { }
  ngOnInit(): void {
    this.initialized = true;
    this.loadedOrderId = this.orderId;
    this.load();
    this.subscriptions.add(this.communication.changes.subscribe(change => {
      if (change.kind !== communicationKinds.read && (!change.orderId || change.orderId === this.orderId))
        this.load();
    }));
  }
  ngOnChanges(): void {
    if (!this.initialized)
      return;
    if (this.loadedOrderId !== this.orderId) {
      this.conversationVersion++;
      this.loadRequest?.unsubscribe();
      this.loading = false;
      this.resetConversation();
      this.loadedOrderId = this.orderId;
    }
    if (this.active)
      this.load();
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.conversationVersion++;
    this.loadRequest?.unsubscribe();
    this.selectedFiles = [];
    this.pendingScroll = undefined;
    this.subscriptions.unsubscribe();
  }
  ngAfterViewChecked(): void {
    const scrollUpdate = this.pendingScroll;
    const timelineElement = this.timeline?.nativeElement;
    if (!scrollUpdate || !timelineElement)
      return;
    this.pendingScroll = undefined;
    if (!this.active)
      return;
    // Measure after Angular has rendered the messages, including coalesced updates.
    timelineElement.scrollTop = scrollUpdate.kind === 'history'
      ? scrollUpdate.top + timelineElement.scrollHeight - scrollUpdate.height
      : timelineElement.scrollHeight;
  }
  load(older = false): void {
    if (!this.active)
      return;
    if (this.loading) {
      if (!older)
        this.refreshPending = true;
      return;
    }
    this.loading = true;
    this.error = '';
    const timelineElement = this.timeline?.nativeElement;
    const shouldScroll = !this.messages.length || (!!timelineElement && timelineElement.scrollHeight - timelineElement.scrollTop - timelineElement.clientHeight < chatDisplay.bottomScrollThresholdPixels);
    const oldHeight = timelineElement?.scrollHeight ?? 0;
    const oldTop = timelineElement?.scrollTop ?? 0;
    const conversationVersion = this.conversationVersion;
    const orderId = this.orderId;
    this.loadRequest = this.communication.messages(orderId, older ? this.messages[0]?.id : undefined).pipe(
      switchMap(page => this.refreshCachedHistory(orderId, page, older).pipe(map(history => ({ page, history }))))
    ).subscribe({
      next: ({ page, history }) => {
        if (conversationVersion !== this.conversationVersion)
          return;
        if (history.some(result => result.clientId !== page.clientId)) {
          this.resetConversation();
          this.loading = false;
          this.load();
          return;
        }
        if (this.conversationClientId && page.clientId !== this.conversationClientId)
          this.resetConversation();
        this.conversationClientId = page.clientId;
        const hasNewMessages = this.mergeMessagePage(page, history, older);
        this.loading = false;
        if (this.active && !document.hidden && page.items.length)
          this.subscriptions.add(this.communication.readMessages(this.orderId, page.items.map(m => m.id))
            .subscribe({ next: () => this.communication.refreshUnread(), error: () => { } }));
        if (older)
          this.pendingScroll = { kind: 'history', height: oldHeight, top: oldTop };
        else if (hasNewMessages || shouldScroll)
          this.pendingScroll = { kind: 'bottom' };
        if (this.refreshPending) {
          this.refreshPending = false;
          this.load();
        }
      },
      error: err => {
        if (conversationVersion !== this.conversationVersion)
          return;
        this.loading = false;
        this.refreshPending = false;
        if (err.status === 403 || err.status === 404)
          this.resetConversation();
        this.error = getApiErrorMessage(err, 'Пораките не може да се вчитаат. Обидете се повторно.');
      }
    });
  }

  private refreshCachedHistory(orderId: string, latestPage: MessagePage, older: boolean): Observable<MessagePage[]> {
    const clientChanged = !!this.conversationClientId && latestPage.clientId !== this.conversationClientId;
    if (older || clientChanged || !latestPage.items.length) return of([]);

    // Older messages may remain cached, but their attachment access and metadata must be revalidated.
    const latestMessageIds = new Set(latestPage.items.map(message => message.id));
    const cachedMessageIds = this.messages.filter(message => !latestMessageIds.has(message.id)).map(message => message.id);
    const historyRequests: Observable<MessagePage>[] = [];
    for (let start = 0; start < cachedMessageIds.length; start += communicationLimits.maximumIdsPerRequest)
      historyRequests.push(this.communication.refreshMessages(orderId,
        cachedMessageIds.slice(start, start + communicationLimits.maximumIdsPerRequest)));

    return historyRequests.length ? forkJoin(historyRequests) : of([]);
  }

  private mergeMessagePage(page: MessagePage, history: MessagePage[], older: boolean): boolean {
    if (!older && !page.items.length) {
      this.messages = [];
      this.hasMore = false;
    }
    if (history.length) {
      const refreshedMessages = new Map(history.flatMap(result => result.items).map(message => [message.id, message]));
      const latestMessageIds = new Set(page.items.map(message => message.id));
      this.messages = this.messages.filter(message => latestMessageIds.has(message.id) || refreshedMessages.has(message.id))
        .map(message => refreshedMessages.get(message.id) ?? message);
    }

    const hasNewMessages = page.items.some(message => !this.messages.some(existing => existing.id === message.id));
    if (older || !this.messages.length) this.hasMore = page.hasMore;
    // A long outage can leave more than one page unseen: reset to a contiguous latest page.
    const hasHistoryGap = !older && this.messages.length > 0 && page.items.length > 0 &&
      !page.items.some(message => this.messages.some(existing => existing.id === message.id));
    if (hasHistoryGap) {
      this.messages = [];
      this.hasMore = page.hasMore;
    }

    const mergedMessages = new Map(this.messages.map(message => [message.id, message]));
    page.items.forEach(message => mergedMessages.set(message.id, message));
    this.messages = [...mergedMessages.values()].sort((first, second) =>
      first.createdAt.localeCompare(second.createdAt) || first.id.localeCompare(second.id));
    return hasNewMessages;
  }
  selectFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = [...(input.files ?? [])];
    input.value = '';
    this.addFiles(files);
  }
  onPaste(event: ClipboardEvent): void {
    const images = [...(event.clipboardData?.items ?? [])]
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile()).filter((file): file is File => !!file);
    if (!images.length)
      return;
    event.preventDefault();
    this.addFiles(images);
  }
  private addFiles(files: File[]): void {
    if (this.sending)
      return;
    if (files.some(file => !file.size || file.size > communicationLimits.maximumAttachmentSizeBytes) ||
        this.selectedFiles.length + files.length > communicationLimits.maximumAttachments) {
      this.error = `Додадете до ${communicationLimits.maximumAttachments} датотеки, секоја до ${communicationLimits.maximumAttachmentSizeBytes / (1024 * 1024)} MB.`;
      return;
    }
    this.error = '';
    this.selectedFiles.push(...files.map(file => ({ file })));
  }
  async send(): Promise<void> {
    const body = this.draft.trim();
    if ((!body && !this.selectedFiles.length) || body.length > communicationLimits.maximumMessageLength || this.sending)
      return;
    const conversationVersion = this.conversationVersion;
    const orderId = this.orderId;
    const selections = [...this.selectedFiles];
    this.sending = true;
    this.error = '';
    try {
      for (const selection of selections) {
        if (!selection.uploaded) {
          selection.uploaded = await firstValueFrom(this.files.uploadMessageAttachment(orderId, selection.file));
        }
        if (this.destroyed || conversationVersion !== this.conversationVersion) {
          return;
        }
      }
      const ids = selections.map(selection => selection.uploaded!.id);
      const signature = JSON.stringify([body, ids]);
      // Reuse the request identity on retries so a lost HTTP response cannot create a duplicate message.
      if (this.pendingSend?.signature !== signature)
        this.pendingSend = { id: crypto.randomUUID(), signature };
      const request = ids.length ? this.communication.send(orderId, this.pendingSend.id, body, ids) : this.communication.send(orderId, this.pendingSend.id, body);
      await firstValueFrom(request);
      if (this.destroyed || conversationVersion !== this.conversationVersion)
        return;
      this.draft = '';
      this.selectedFiles = [];
      this.pendingSend = undefined;
      this.communication.changes.next({ orderId, kind: communicationKinds.file });
      this.load();
    }
    catch (err) {
      if (!this.destroyed && conversationVersion === this.conversationVersion)
        this.error = getApiErrorMessage(err, 'Пораката не е испратена. Обидете се повторно.');
    }
    finally {
      if (conversationVersion === this.conversationVersion)
        this.sending = false;
    }
  }
  removeFile(index: number): void {
    if (this.sending)
      return;
    this.selectedFiles.splice(index, 1);
    this.pendingSend = undefined;
  }
  private resetConversation(): void {
    this.conversationVersion++;
    this.sending = false;
    this.messages = [];
    this.hasMore = false;
    this.conversationClientId = undefined;
    this.pendingScroll = undefined;
    this.refreshPending = false;
    this.pendingSend = undefined;
    this.draft = '';
    this.selectedFiles = [];
  }
  onKey(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.keyCode === 229)
      return;
    event.preventDefault();
    if (!event.repeat)
      this.send();
  }
}
